import { Popover, Tab, Transition } from '@headlessui/react';
import {
  BugAntIcon,
  PhoneIcon,
  ShoppingCartIcon,
  WrenchIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import CanAccess from 'components/auth/can-access';
import ProgressRing from 'components/common/progress-ring';
import SendZoomUrlModal from 'components/layout/send-zoom-url-modal';
import Tooltip from 'components/layout/tooltip';
import ShoppingCart from 'components/shopping-cart/shopping-cart';
import { tabGatingMessages, tabLabels } from 'data/constants';
import { SYSTEM_SELECTOR_ADMIN_ROLE } from 'data/roles';
import { ActiveTabType } from 'data/types';
import { setSelectedDuctedSystems, setSelectedSystems } from 'features/systems';
import { setActiveTab, setShowDebugMenu } from 'features/ui';
import useEquipmentPreferencesPageVisible from 'hooks/use-equipment-preferences-page-visible';
import useExistingEquipmentPageVisible from 'hooks/use-existing-equipment-page-visible';
import {
  useHasQuotableSystems,
  useHasQuotedSystems
} from 'hooks/use-quoted-unique-ids';
import { useSizeSkipEligible } from 'hooks/use-size-skip';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import useTabLocks from 'hooks/use-tab-locks';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { isChooseSystemsPrerequisiteComplete } from 'utils/choose-systems-gate';
import { usesDuctedAccessoryRules } from 'utils/ducted-accessory-rules';
import { projectContinueTab } from 'utils/existing-equipment-page-visibility';
import { equipmentStepProceedMessage } from 'utils/existing-equipment-steps';
import { enableHorizontalScroll } from 'utils/helpers';
import { isSystemDesignLocked } from 'utils/system-design-bypass';
import { warningModal } from 'utils/ui';
import { getVisibleTabs } from 'utils/visible-tabs';

export default function Header() {
  const dispatch = useAppDispatch();
  const { selectedSystems, selectedDuctedSystems } = useAppSelector(
    (state) => state.systems
  );
  const { activeTab, pageProgress } = useAppSelector((state) => state.ui);
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const { systemSelectorZoomUrl } = useAppSelector((state) => state.auth);
  const [showCartIndicator, setShowCartIndicator] = useState(false);
  const [zoomUrlModalOpen, setZoomUrlModalOpen] = useState(false);

  const hasQuotableSystems = useHasQuotableSystems();
  const hasQuotedSystems = useHasQuotedSystems();
  const hasSelectedSystems =
    selectedSystems.length > 0 || selectedDuctedSystems.length > 0;
  const canAccessorize =
    hasQuotableSystems || hasSelectedSystems || hasQuotedSystems;
  const showExistingEquipmentTab = useExistingEquipmentPageVisible();
  const showEquipmentPreferencesTab = useEquipmentPreferencesPageVisible();
  const hasSystemsUsingDuctedAccessoryRules = selectedSystems.some(
    usesDuctedAccessoryRules
  );
  // AHA-6681 tonnage §F: while the L&S skip is eligible, System Design
  // carries an 'Optional' badge. PRESENTATIONAL only — the tab gating and
  // page-progress semantics above/below are untouched.
  const tonnageSkipEligible = useSizeSkipEligible();
  // System Design bypass (utils/system-design-bypass): on the strict
  // ducted-only path System Design is hidden (recommendations come
  // straight from Existing Equipment). On the hybrid path with no mini-split
  // rooms System Design is hidden too — the whole-house configuration is
  // generated in the background and the normal Choose Systems / Add-Ons
  // flow runs off it. Hidden tabs come back the moment the mode clears.
  const bypassMode = useSystemDesignBypassMode();
  const tabLocks = useTabLocks();
  const systemDesignLocked = isSystemDesignLocked(bypassMode);
  const chooseSystemsPrerequisiteComplete = isChooseSystemsPrerequisiteComplete(
    { systemDesignLocked, pageProgress }
  );

  useEffect(() => {
    if (activeTab === 'existingEquipment' && !showExistingEquipmentTab) {
      dispatch(
        setActiveTab(
          projectContinueTab(
            showExistingEquipmentTab,
            showEquipmentPreferencesTab
          )
        )
      );
    } else if (
      activeTab === 'equipmentPreferences' &&
      !showEquipmentPreferencesTab
    ) {
      dispatch(setActiveTab('structure'));
    }
  }, [
    activeTab,
    showExistingEquipmentTab,
    showEquipmentPreferencesTab,
    dispatch
  ]);

  useEffect(() => {
    if (
      selectedSystems.length > 0 ||
      selectedSystems.some((system) =>
        system.selectedAccessories.some((acc) => acc.quantity > 0)
      )
    ) {
      setShowCartIndicator(true);
    } else {
      setShowCartIndicator(false);
    }
  }, [
    selectedSystems.length,
    JSON.stringify(selectedSystems.map((system) => system.selectedAccessories))
  ]);

  const removeEmptyAccessoriesFromSystems = () => {
    setSelectedSystems(
      produce(selectedSystems, (draft) => {
        draft.forEach((system) => {
          // eslint-disable-next-line no-param-reassign
          system.selectedAccessories = system.selectedAccessories.filter(
            (acc) => acc.quantity > 0
          );
        });
      })
    );
    setSelectedDuctedSystems(
      produce(selectedDuctedSystems, (draft) => {
        draft.forEach((system) => {
          // eslint-disable-next-line no-param-reassign
          system.selectedAccessories = system.selectedAccessories.filter(
            (acc) => acc.quantity > 0
          );
        });
      })
    );
  };

  const handleTabClick = (tab: ActiveTabType) => {
    // Locked tabs can never reach 100 while their bypass mode is on, so
    // fallback messages must not steer users at them.
    const furthestTab = Object.keys(pageProgress).find(
      (key) => pageProgress[key] < 100 && !(key in tabLocks)
    ) as ActiveTabType;
    const lock = tabLocks[tab];

    switch (tab) {
      case 'project':
        dispatch(setActiveTab(tab));
        break;
      case 'existingEquipment':
        if (
          pageProgress.project === 100 ||
          pageProgress.existingEquipment === 100
        ) {
          dispatch(setActiveTab(tab));
        } else {
          warningModal(
            equipmentStepProceedMessage({
              tab: furthestTab,
              existingSystems
            })
          );
        }
        break;
      case 'equipmentPreferences':
        if (
          pageProgress.existingEquipment === 100 ||
          pageProgress.equipmentPreferences === 100
        ) {
          dispatch(setActiveTab(tab));
        } else {
          warningModal(
            equipmentStepProceedMessage({
              tab: furthestTab,
              existingSystems
            })
          );
        }
        break;
      case 'structure':
        if (
          (pageProgress.existingEquipment === 100 &&
            pageProgress.equipmentPreferences === 100) ||
          pageProgress.structure === 100
        ) {
          dispatch(setActiveTab(tab));
        } else {
          warningModal(
            equipmentStepProceedMessage({
              tab: furthestTab,
              existingSystems
            })
          );
        }
        break;
      case 'configurations':
        if (lock) {
          warningModal(lock.message);
        } else if (
          pageProgress.structure === 100 ||
          pageProgress.configurations === 100
        ) {
          dispatch(setActiveTab(tab));
        } else {
          warningModal(
            equipmentStepProceedMessage({
              tab: furthestTab,
              existingSystems
            })
          );
        }
        break;
      case 'chooseSystems':
        if (lock) {
          warningModal(lock.message);
        } else if (
          chooseSystemsPrerequisiteComplete ||
          pageProgress.chooseSystems === 100
        ) {
          dispatch(setActiveTab(tab));
        } else {
          warningModal(
            equipmentStepProceedMessage({
              tab: furthestTab,
              existingSystems
            })
          );
        }
        break;
      case 'accessories':
        if (lock) {
          warningModal(lock.message);
        } else if (
          (pageProgress.chooseSystems === 100 ||
            pageProgress.accessories === 100 ||
            hasSystemsUsingDuctedAccessoryRules) &&
          canAccessorize
        ) {
          dispatch(setActiveTab(tab));
        } else if (!canAccessorize) {
          warningModal(tabGatingMessages.noQuotableSystems);
        } else {
          warningModal(
            equipmentStepProceedMessage({
              tab: furthestTab,
              existingSystems
            })
          );
        }
        break;
      case 'quote':
        if (
          pageProgress.accessories === 100 ||
          pageProgress.quote === 100 ||
          hasSystemsUsingDuctedAccessoryRules
        ) {
          dispatch(setActiveTab(tab));
        } else if (!hasQuotableSystems) {
          warningModal(tabGatingMessages.noQuotableSystems);
        } else {
          warningModal(
            equipmentStepProceedMessage({
              tab: furthestTab,
              existingSystems
            })
          );
        }
        break;
      default:
        break;
    }
  };

  const handleShoppingCartClick = () => {
    setShowCartIndicator(false);
  };

  const tabClasses = (active: boolean, disabled: boolean, isLast: boolean) =>
    clsx(
      'h-full flex items-center gap-3 border-b border-gray-200 pl-8 -ml-5 group',
      active && 'text-black bg-neutral-100',
      disabled && 'bg-white text-gray-400 cursor-not-allowed',
      !active && !disabled && 'bg-white text-gray-700 hover:bg-gray-50',
      isLast && 'pr-5'
    );

  return (
    <div className='flex h-12 min-h-12 w-full bg-white'>
      <Tab.List
        className='hidden-scrollbar flex overflow-x-auto'
        onWheel={enableHorizontalScroll}
      >
        {getVisibleTabs({
          showExistingEquipmentTab,
          showEquipmentPreferencesTab,
          systemDesignLocked
        }).map((tab) => {
          let enabled = false;
          let nextTab: ActiveTabType = 'structure';
          const locked = tab in tabLocks;

          switch (tab) {
            case 'project':
              enabled = true;
              nextTab = projectContinueTab(
                showExistingEquipmentTab,
                showEquipmentPreferencesTab
              );
              break;
            case 'existingEquipment':
              enabled =
                pageProgress.project === 100 ||
                pageProgress.existingEquipment === 100;
              nextTab = 'equipmentPreferences';
              break;
            case 'equipmentPreferences':
              enabled =
                pageProgress.existingEquipment === 100 ||
                pageProgress.equipmentPreferences === 100;
              nextTab = 'structure';
              break;
            case 'structure':
              enabled =
                (pageProgress.existingEquipment === 100 &&
                  pageProgress.equipmentPreferences === 100) ||
                pageProgress.structure === 100;
              nextTab = systemDesignLocked ? 'chooseSystems' : 'configurations';
              break;
            case 'configurations':
              enabled =
                pageProgress.structure === 100 ||
                pageProgress.configurations === 100;
              nextTab = 'chooseSystems';
              break;
            case 'chooseSystems':
              enabled =
                !locked &&
                (chooseSystemsPrerequisiteComplete ||
                  pageProgress.chooseSystems === 100);
              nextTab = 'accessories';
              break;
            case 'accessories':
              enabled =
                (pageProgress.chooseSystems === 100 ||
                  pageProgress.accessories === 100 ||
                  hasSystemsUsingDuctedAccessoryRules) &&
                canAccessorize;
              nextTab = 'quote';
              break;
            case 'quote':
              enabled =
                pageProgress.accessories === 100 ||
                pageProgress.quote === 100 ||
                hasSystemsUsingDuctedAccessoryRules;
              break;
            default:
              break;
          }
          return (
            <Tab
              key={tab}
              className='h-full focus:outline-none'
              onClick={() => handleTabClick(tab)}
            >
              <div
                className={tabClasses(
                  activeTab === tab,
                  !enabled && activeTab !== tab,
                  tab === 'quote'
                )}
              >
                <ProgressRing progress={pageProgress[tab]} />
                <span className='whitespace-nowrap font-semibold'>
                  {tabLabels[tab]}
                </span>
                {tonnageSkipEligible && tab === 'configurations' && (
                  <span className='whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500'>
                    Optional
                  </span>
                )}
                {tab !== 'quote' && (
                  <div className='relative h-full w-5'>
                    <div
                      className={`absolute inset-0 ${
                        activeTab !== nextTab && 'group-hover:bg-white'
                      } ${activeTab === nextTab && 'bg-neutral-100'}`}
                    />
                    <div className='absolute inset-0'>
                      <svg
                        className={`fill h-full w-full text-gray-300 ${
                          // eslint-disable-next-line no-nested-ternary
                          activeTab === tab
                            ? 'fill-neutral-100'
                            : enabled
                            ? 'fill-white group-hover:fill-gray-50'
                            : 'fill-white'
                        }`}
                        viewBox='0 0 22 80'
                        preserveAspectRatio='none'
                      >
                        <path
                          d='M0 -2L20 40L0 82'
                          vectorEffect='non-scaling-stroke'
                          stroke='currentcolor'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </Tab>
          );
        })}
      </Tab.List>
      <div className='h-full grow border-b border-gray-200' />
      {(selectedDuctedSystems.length > 0 || selectedSystems.length > 0) && (
        <Popover className='relative'>
          <Popover.Button
            className='relative flex h-full shrink-0 cursor-pointer items-center border-b border-l border-gray-200 px-3 text-gray-900 hover:bg-gray-50'
            onClick={handleShoppingCartClick}
          >
            <div className='relative'>
              <ShoppingCartIcon className='h-6 w-6' />
              {showCartIndicator && (
                <div className='absolute -right-1 -top-1 h-[10px] w-[10px] rounded-full bg-blue-500' />
              )}
            </div>
            <Tooltip text='Shopping Cart' />
          </Popover.Button>
          <Transition
            afterLeave={removeEmptyAccessoriesFromSystems}
            enter='absolute z-40 transition ease-out duration-200'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='absolute z-40 transition ease-in duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Popover.Panel className='absolute z-40'>
              <ShoppingCart />
            </Popover.Panel>
          </Transition>
        </Popover>
      )}
      <button
        type='button'
        aria-disabled={!systemSelectorZoomUrl}
        className={clsx(
          'relative flex h-full shrink-0 items-center border-b border-l border-gray-200 px-3',
          systemSelectorZoomUrl
            ? 'cursor-pointer text-gray-900 hover:bg-gray-50'
            : 'cursor-not-allowed text-gray-300'
        )}
        onClick={() => {
          if (systemSelectorZoomUrl) {
            setZoomUrlModalOpen(true);
          }
        }}
      >
        <PhoneIcon className='h-6 w-6' />
        <Tooltip
          text={
            systemSelectorZoomUrl
              ? 'Send Zoom Link to Customer'
              : 'No Zoom URL found for user'
          }
        />
      </button>
      <button
        type='button'
        className='relative flex h-full shrink-0 cursor-pointer items-center border-b border-l border-gray-200 px-3 text-gray-900 hover:bg-gray-50'
        onClick={() => dispatch(setShowDebugMenu(true))}
      >
        <BugAntIcon className='h-6 w-6' />
        <Tooltip text='Debug Menu' />
      </button>
      <CanAccess role={SYSTEM_SELECTOR_ADMIN_ROLE}>
        <a
          href={`/admin${window.location.search}`}
          className='relative flex h-full shrink-0 cursor-pointer items-center border-b border-l border-gray-200 px-3 text-gray-900 hover:bg-gray-50'
        >
          <WrenchIcon className='h-6 w-6' />
          <Tooltip text='Admin Panel' />
        </a>
      </CanAccess>
      <SendZoomUrlModal openState={[zoomUrlModalOpen, setZoomUrlModalOpen]} />
    </div>
  );
}
